/**
 * One finding as one sentence.
 *
 * Two kinds of finding land in the same list and they are rendered from two
 * different places on purpose:
 *
 *  - a MEASURED finding (links, pages, layout) is a catalogue entry with numbers
 *    poured into it, so it reads in the operator's own language and can be fixed
 *    for every past run by editing one JSON file;
 *  - an AI finding (content, vision) carries the sentence the model wrote about
 *    this one website. That sentence cannot be a translation key - it is about
 *    taufauto.ee's delivery heading, not about headings in general - so it is
 *    shown verbatim and the `kind` stays as the category label beside it.
 *
 * Shared rather than duplicated because the arguments tab quotes the same
 * findings under each claim, and two renderers drifting apart would mean the
 * proof under an argument disagreeing with the proof in the findings list.
 */
import type { BrainFinding } from "../../../../lib/siteBrain";
import type { T } from "../../site-scanner/types";

/** True when the sentence came from a model rather than from the catalogue. */
export function isWritten(finding: BrainFinding): boolean {
  return finding.source === "content" || finding.source === "vision";
}

/**
 * The sentence to show. Never a bare slug: an untranslated key helps nobody.
 *
 * The empty-text branch is not theoretical. A vision answer may carry an empty
 * note - the schema caps its length but does not demand one - and an AI finding
 * has no catalogue entry to fall back on, so `brain.finding.<kind>` would print
 * the raw slug to an operator about to read it down a phone. The category label
 * is a true, if shorter, sentence about the same defect.
 */
export function findingText(finding: BrainFinding, t: T): string {
  if (finding.text) return finding.text;
  if (isWritten(finding)) return t(`brain.check.${finding.kind}`);
  return t(`brain.finding.${finding.kind}`, finding.vars);
}

/** The short category label for an AI finding, or null for a measured one. */
export function findingCategory(finding: BrainFinding, t: T): string | null {
  return isWritten(finding) ? t(`brain.check.${finding.kind}`) : null;
}
